'use client';

import { ArrowUpRight, Github, ExternalLink, FileText } from 'lucide-react';
import { useTranslation } from '@/context/I18nContext';
import { getProjectsData } from '@/data/loader';
import { modes } from '@/components/shared/config';
import { trackEvent } from '@/lib/umami';
import Link from 'next/link';

export default function ProjectsPage() {
  const { locale, t } = useTranslation();
  const projData = getProjectsData(locale);
  const mode = 'pm';
  const design = modes[0].design; // PM mode design

  const handleProjectLinkClick = (projectId: string, linkType: string) => {
    trackEvent('project_link_click', {
      project_id: projectId,
      link_type: linkType,
      context: 'all_projects_page',
      mode,
      locale,
    });
  };

  return (
    <div className={`min-h-screen transition-all duration-500 ease-in-out ${design.theme} font-sans relative`}>
      <div className="fixed inset-0 grid-pattern-pm pointer-events-none z-0" />
      <div className="relative z-10">
        <div className="mx-auto max-w-[1200px] px-6 py-12 lg:px-12 lg:py-16">
          {/* Header */}
          <div className="mb-16">
            <h1 className={`text-6xl font-bold tracking-tight mb-4 ${design.textPrimary}`}>
              {t('projectsPage.title')}
            </h1>
            <p className={`text-lg ${design.textSecondary}`}>
              {t('projectsPage.subtitle')}
            </p>
            <Link
              href="/"
              className={`inline-flex items-center gap-2 mt-6 text-sm font-medium ${design.accent} hover:opacity-80 transition`}
            >
              {t('projectsPage.backToHome')}
            </Link>
          </div>

          {/* Projects Grid */}
          <div className="grid gap-12">
            {(projData as Array<{id: string; title: string; year: string; links?: Record<string, string>; desc: string; tech: string[]}>).map((project) => (
              <article
                key={project.id}
                id={`proj-${project.id}`}
                data-sublayer-id={`proj-${project.id}`}
                data-sublayer-name={project.title}
                className={`group relative rounded-lg p-8 border transition-all duration-300 ${
                  design.cardBg
                } ${design.cardBorder} hover:border-slate-300 hover:shadow-lg`}
              >
                <div className="mb-6 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className={`text-2xl font-bold ${design.textPrimary}`}>
                      {project.title}
                    </h3>
                    <span className={`text-xs ${design.textSecondary} opacity-70`}>
                      {project.year}
                    </span>
                  </div>

                  <div className="flex gap-3 justify-end">
                    {project.links?.case && (
                      <a
                        href={project.links.case}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 rounded-full transition-colors bg-slate-100 hover:bg-slate-200 text-slate-700"
                        aria-label={t('accessibility.openCaseStudy', { title: project.title })}
                        onClick={() => handleProjectLinkClick(project.id, 'case')}
                      >
                        <ArrowUpRight className="h-5 w-5" aria-hidden="true" />
                      </a>
                    )}
                    {project.links?.repo && (
                      <a
                        href={project.links.repo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 rounded-full transition-colors bg-slate-100 hover:bg-slate-200 text-slate-700"
                        aria-label={t('accessibility.openRepository', { title: project.title })}
                        onClick={() => handleProjectLinkClick(project.id, 'repo')}
                      >
                        <Github className="h-5 w-5" aria-hidden="true" />
                      </a>
                    )}
                    {project.links?.demo && (
                      <a
                        href={project.links.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 rounded-full transition-colors bg-slate-100 hover:bg-slate-200 text-slate-700"
                        aria-label={t('accessibility.openDemo', { title: project.title })}
                        onClick={() => handleProjectLinkClick(project.id, 'demo')}
                      >
                        <ExternalLink className="h-5 w-5" aria-hidden="true" />
                      </a>
                    )}
                    {project.links?.pdf && (
                      <a
                        href={project.links.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 rounded-full transition-colors bg-slate-100 hover:bg-slate-200 text-slate-700"
                        aria-label={t('accessibility.openPdf', { title: project.title })}
                        onClick={() => handleProjectLinkClick(project.id, 'pdf')}
                      >
                        <FileText className="h-5 w-5" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </div>

                <p className={`text-base leading-7 mb-8 ${design.textSecondary}`}>
                  {project.desc}
                </p>

                <div className={`flex flex-wrap gap-2 border-t pt-6 ${design.border} opacity-30`}>
                  {project.tech.map((t: string) => (
                    <span key={t} className="text-xs font-medium px-2 py-1 rounded bg-slate-100 text-slate-600">
                      #{t}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
